// Verified ADMALL INDIA Industries Data Repository

export const INDUSTRIES = [
  {
    slug: "real-estate",
    title: "Real Estate",
    tagline: "Project branding, property walkthrough films, and high-intent buyer acquisition.",
    heroImage: "https://images.unsplash.com/photo-1723110994499-df46435aa4b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "Real estate buyers do not purchase square footage; they invest in certainty, prestige, and lifestyle. From initial project identity and architectural walkthrough films to hyper-local advertising and site-visit generation, we build the marketing engine that sells properties.",
    challenges: [
      "High competition with neighboring developments running nearly indistinguishable ads.",
      "Costly digital leads that turn out to be unqualified or outside the project's ticket size.",
      "Disconnect between digital brochures and on-ground experiential site galleries.",
      "Extended booking cycles requiring persistent, multi-channel nurturing."
    ],
    deliverables: [
      {
        title: "Project Naming, Identity & Launch Collateral",
        desc: "Distinctive architectural branding that sets your development apart."
      },
      {
        title: "Cinematic Property & Aerial Drone Walkthroughs",
        desc: "Stunning 4K video production highlighting connectivity, views, and lifestyle amenities."
      },
      {
        title: "Hyper-Local Catchment Marketing",
        desc: "Geo-targeted campaigns directed at high-net-worth neighborhoods and corporate corridors."
      },
      {
        title: "Chat2Customer™ WhatsApp Lead Funnels",
        desc: "Instant budget and configuration pre-qualification before routing to your sales desk."
      },
      {
        title: "NRI & Outstation Investor Outreach",
        desc: "Targeted digital campaigns in key diaspora hubs and Tier-2 investor centers."
      },
      {
        title: "Site-Visit Booking Funnels",
        desc: "Frictionless scheduling and automated reminder flows ensuring high visit attendance."
      }
    ],
    strategicInsight:
      "Real estate campaigns fail when they treat leads as mere numbers. We combine aspirational visual production with our Chat2Customer™ system to qualify buyer budget and possession timeline before handing them to your sales gallery.",
    relatedServices: ["multimedia-production", "performance-lead-generation", "brand-engineering"]
  },
  {
    slug: "manufacturing-b2b",
    title: "Manufacturing & B2B",
    tagline: "Industrial corporate identity, capability films, and distributor enquiry engines.",
    heroImage: "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "B2B procurement teams evaluate technical precision, operational infrastructure, and financial reliability. We transform complex manufacturing engineering into commanding corporate visual stories, high-ranking Google search presence, and sustained procurement inquiries.",
    challenges: [
      "Relying solely on word-of-mouth and trade shows while competitors capture online discovery.",
      "Outdated corporate catalogs and website imagery that fail to reflect physical engineering scale.",
      "Long procurement sales cycles with multiple technical and financial stakeholders.",
      "Difficulty demonstrating precision manufacturing without in-person factory visits."
    ],
    deliverables: [
      {
        title: "Corporate Rebranding & Industrial Catalog Design",
        desc: "Authoritative brand systems, technical datasheets, and presentation collateral."
      },
      {
        title: "Factory & Operational Walkthrough Films",
        desc: "High-definition documentation of machinery, cleanrooms, QA protocols, and capacity."
      },
      {
        title: "B2B High-Intent Search (SEO & Google Ads)",
        desc: "Capturing procurement managers searching for OEM, contract manufacturing, and industrial suppliers."
      },
      {
        title: "LinkedIn Thought Leadership & ABM",
        desc: "Positioning leadership and technical excellence directly before decision-makers."
      },
      {
        title: "Distributor & Dealership Recruitment Funnels",
        desc: "Targeted regional outreach to expand dealer distribution networks."
      },
      {
        title: "Technical Sales Enablement Collateral",
        desc: "Equipping your business development team with convincing pitch decks and case summaries."
      }
    ],
    strategicInsight:
      "Industrial decision-makers conduct extensive digital diligence before contacting suppliers. We ensure your digital presence commands immediate confidence and accurately reflects your manufacturing strength.",
    relatedServices: ["brand-engineering", "multimedia-production", "digital-marketing"]
  },
  {
    slug: "d2c",
    title: "D2C & Consumer Brands",
    tagline: "Brand positioning, dynamic product content, and scalable customer acquisition.",
    heroImage: "https://images.unsplash.com/photo-1728464864608-98006d66f075?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "In consumer retail, differentiation is survival. We craft distinctive product identities, produce scroll-stopping social creative, and implement disciplined performance advertising models that acquire customers profitably.",
    challenges: [
      "Rising customer acquisition costs (CAC) eroding margin across Meta and Google.",
      "High ad creative fatigue requiring constant generation of fresh video angles.",
      "Fierce marketplace competition from legacy brands and low-cost alternatives.",
      "Weak initial customer retention and low repeat purchase rates."
    ],
    deliverables: [
      {
        title: "Product Positioning & Packaging Visual Design",
        desc: "Memorable shelf presence and unboxing aesthetics that build brand equity."
      },
      {
        title: "Commercial Studio Product Cinematography",
        desc: "High-fidelity texture, application, and benefit-driven video demonstrations."
      },
      {
        title: "High-Volume Hook Testing & Social Video",
        desc: "Testing diverse creative angles to discover top-performing customer acquisition hooks."
      },
      {
        title: "Meta & Google Performance Shopping Campaigns",
        desc: "Disciplined ROAS-driven paid acquisition with rigorous attribution tracking."
      },
      {
        title: "Trial2Buy™ Sampling & Impulse Purchase Architectures",
        desc: "Low-friction sampling strategies that convert curious shoppers into loyal buyers."
      },
      {
        title: "Customer Lifetime Value (LTV) Workflows",
        desc: "Automated retention and replenishment journeys across WhatsApp and email."
      }
    ],
    strategicInsight:
      "Scaling direct-to-consumer requires ruthless creative iteration. We deploy rapid variation testing to uncover the exact visual hooks and emotional angles that drive conversions at the lowest customer acquisition cost.",
    relatedServices: ["multimedia-production", "performance-lead-generation", "ai-powered-marketing"]
  },
  {
    slug: "healthcare",
    title: "Healthcare & Clinics",
    tagline: "Trust-first medical branding, local visibility, and patient appointment generation.",
    heroImage: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "Healthcare marketing demands absolute empathy, strict medical ethics, and unshakeable community credibility. We elevate hospitals, multi-specialty clinics, and specialized practitioners through educational content, local search prominence, and streamlined appointment access.",
    challenges: [
      "Patient hesitation and skepticism toward aggressive commercial advertising.",
      "Poor local search rankings causing nearby patients to choose competing clinics.",
      "Complex clinical terminology confusing patients rather than building confidence.",
      "High appointment drop-off rates and missed consultations."
    ],
    deliverables: [
      {
        title: "Clinic & Hospital Brand Architecture",
        desc: "Clean, reassuring visual identity and clear patient-facing collateral."
      },
      {
        title: "Doctor Educational Video Storytelling",
        desc: "Clear, approachable video explainers addressing common symptoms and treatment options."
      },
      {
        title: "Google Business Profile & Hyper-Local Search",
        desc: "Dominating local map packs and high-intent searches like 'specialist near me'."
      },
      {
        title: "LocalVoice™ Vernacular Healthcare Campaigns",
        desc: "Culturally resonant regional-language campaigns connecting with local families."
      },
      {
        title: "Direct WhatsApp Appointment Booking",
        desc: "Instant, friendly booking flows that reduce friction for patients and staff."
      },
      {
        title: "Patient Review & Reputation Management",
        desc: "Systematic collection of authentic patient reviews to build enduring local trust."
      }
    ],
    strategicInsight:
      "Patients choose healthcare providers based on local proximity and deep clinical trust. Our LocalVoice™ framework tailors messaging into regional languages and authentic doctor-patient conversations.",
    relatedServices: ["digital-marketing", "performance-lead-generation", "multimedia-production"]
  },
  {
    slug: "education",
    title: "Education & Institutions",
    tagline: "Institutional branding, campus films, and admission cycle enrollment campaigns.",
    heroImage: "https://images.pexels.com/photos/30052431/pexels-photo-30052431.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200",
    summary:
      "Education represents one of the most critical decisions a family makes. We help universities, K-12 schools, and coaching academies articulate their academic pedigree, showcase campus culture, and consistently fulfill admission targets across annual recruitment cycles.",
    challenges: [
      "Intense seasonal pressure to meet enrollment numbers within brief admission windows.",
      "Generic institutional brochures failing to convey modern teaching methods and faculty quality.",
      "High volume of uncommitted inquiries wasting admission counselors' valuable time.",
      "Geographic limitations in expanding reach to students across Tier-2 and Tier-3 towns."
    ],
    deliverables: [
      {
        title: "Institutional Brand Positioning & Prospectus Design",
        desc: "Elevating academic vision, accreditations, and placement records."
      },
      {
        title: "Cinematic Campus Tours & Aerial Films",
        desc: "Immersive visual showcases of labs, sports facilities, hostels, and student life."
      },
      {
        title: "Admission Cycle Paid Media Campaigns",
        desc: "Laser-focused Google Search and Meta Ads synchronised with critical application deadlines."
      },
      {
        title: "Parent & Student Counseling Booking Funnels",
        desc: "Automated booking for campus open days, virtual tours, and counseling calls."
      },
      {
        title: "Alumni Success Story Vignettes",
        desc: "Compelling proof of career outcomes through verified graduate narratives."
      },
      {
        title: "Regional Vernacular Outreach",
        desc: "Targeted campaigns in regional feeder districts communicating in parents' native tongue."
      }
    ],
    strategicInsight:
      "Education marketing revolves around seasonal admission cycles. We build momentum months in advance, positioning institutions as the premier choice before peak decision deadlines.",
    relatedServices: ["brand-engineering", "multimedia-production", "performance-lead-generation"]
  },
  {
    slug: "hospitality",
    title: "Hospitality & Dining",
    tagline: "Atmospheric storytelling, culinary cinematography, and direct booking campaigns.",
    heroImage: "https://images.unsplash.com/photo-1758714919725-d2740fc99f14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "Hospitality is pure sensory appeal and emotional invitation. We bring resorts, boutique hotels, and premier restaurants to life through lush cinematography, culinary photography, and targeted digital campaigns that drive direct reservations and banquet inquiries.",
    challenges: [
      "Excessive reliance on high-commission OTAs eroding profitability on direct bookings.",
      "Visual content that fails to capture the physical atmosphere and dining excellence.",
      "Midweek occupancy dips and seasonal off-peak revenue slumps.",
      "Ineffective influencer outreach that yields free meals without generating measurable guests."
    ],
    deliverables: [
      {
        title: "Resort & Restaurant Brand Identity Systems",
        desc: "Refined menus, collateral, and environmental branding conveying premium luxury."
      },
      {
        title: "Cinematic Food & Beverage Videography",
        desc: "Artful culinary captures highlighting signature dishes and artisanal preparation."
      },
      {
        title: "TrustCircle™ Verified Food & Travel Creators",
        desc: "Contracting genuine regional creators whose followers actually visit and dine."
      },
      {
        title: "Google Maps & Tourist Discovery Optimization",
        desc: "Ensuring top ranking when travelers and food enthusiasts search nearby."
      },
      {
        title: "Direct Booking & Staycation Promos",
        desc: "Profitable direct-booking engines bypassing third-party OTA commissions."
      },
      {
        title: "Banquet & Wedding Venue Marketing",
        desc: "High-ticket lead generation for corporate retreats, banquets, and celebrations."
      }
    ],
    strategicInsight:
      "We deploy our TrustCircle™ network to engage authentic regional creators who visit, experience, and showcase your property to genuinely relevant local and travel audiences.",
    relatedServices: ["multimedia-production", "digital-marketing", "brand-engineering"]
  },
  {
    slug: "entertainment",
    title: "Entertainment & Events",
    tagline: "Event marketing, artist branding, teaser campaigns, and ticket sale acceleration.",
    heroImage: "https://images.unsplash.com/photo-1619229724167-220710df9ac2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "From premier cultural galas like the Satellite City Awards to theatrical releases, artist tours, and brand summits, we build high-voltage marketing campaigns that ignite public anticipation, sell tickets, and fill auditoriums.",
    challenges: [
      "Short promotional windows requiring intense public momentum in a matter of weeks.",
      "High customer drop-off on ticketing platforms without dynamic retargeting.",
      "Aligning sponsor visibility requirements with genuine attendee entertainment.",
      "Managing live multi-camera coverage and rapid post-event social distribution."
    ],
    deliverables: [
      {
        title: "Event Branding, Key Visuals & Stage Systems",
        desc: "Visual branding, hoardings, standees, and stage backdrop architectures."
      },
      {
        title: "Cinematic Teasers, Trailers & Promo Reels",
        desc: "Fast-cut, adrenaline-charged preview video campaigns designed for viral reach."
      },
      {
        title: "Celebrity, Artist & Influencer Coordination",
        desc: "Curating talent amplification across digital channels and red-carpet appearances."
      },
      {
        title: "Digital Ticket Sales & Pass Distribution",
        desc: "Conversion-optimized registration funnels and automated attendee ticketing."
      },
      {
        title: "Corporate Sponsor Pitch Decks",
        desc: "Professional partnership decks demonstrating verifiable crowd reach and ROI."
      },
      {
        title: "On-Ground Multi-Camera Production",
        desc: "Live multicam coverage, backstage highlights, and instant social reel delivery."
      }
    ],
    strategicInsight:
      "Having co-organized major events such as the Satellite City Awards at CIDCO Auditorium, we understand the exact cadence of teaser hype, influencer amplification, and day-of-event media production needed for maximum turnout.",
    relatedServices: ["multimedia-production", "brand-engineering", "digital-marketing"]
  }
];

const ALIASES = {
  "d2c-consumer-brands": "d2c",
  "healthcare-clinics": "healthcare",
  "education-institutions": "education",
  "hospitality-dining": "hospitality",
  "entertainment-events": "entertainment",
};

export function getIndustryBySlug(slug) {
  if (!slug) return null;
  const clean = slug.toLowerCase().trim();
  const target = ALIASES[clean] || clean;
  return INDUSTRIES.find((i) => i.slug === target) || null;
}
